// components/Footer.js
"use client";
import Image from "next/image";
import Link from "next/link";
import logo from "../public/images/logo.webp";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import instagramIcon from "../public/instalogo.png";
import facebookIcon from "../public/faceBooklogo.png";
import twitterIcon from "../public/twitterLogo.png";
import youtubeIcon from "../public/youtubeLogo.png";
import dayjs from "dayjs";
import { getCopyrightHTML } from "@/utils/constants";
import { Routes } from "@/config/routes";

const HomePageFooter = () => {
  const pathname = usePathname();
  const router = useRouter();
  const handleClick = () => {
    if (pathname === "/") {
      // Already on homepage → scroll smoothly
      const topElement = document.getElementById("top");
      if (topElement) {
        topElement.scrollIntoView({ behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      // Not on homepage → navigate to "/"
      router.push("/");
    }
  };
  const searchParams = useSearchParams();

  useEffect(() => {
    const sectionId = searchParams.get("scroll");
    if (sectionId) {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [searchParams]);

  const scrollToSection = (id) => {
    if (pathname === "/") {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
      } else {
        console.warn("Element not found with id:", id);
      }
    } else {
      router.push(`/?scroll=${id}`);
    }
  };
  return (
    <footer className="bg-white border-t border-gray-200 py-6 sm:py-8 flex flex-col w-full">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 w-full">
        {/* Main Footer Content */}
        <div className="flex flex-col md:flex-row justify-between gap-8 md:gap-12">
          {/* Logo and Description */}
          <div className="flex flex-col mb-4 md:mb-0 lg:mr-16">
            <div className="flex items-center mb-3">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mr-2 relative overflow-hidden">
                <Image
                  src={logo}
                  alt="Logo"
                  width={64}
                  height={64}
                  className="rounded-full object-fill"
                />
              </div>
              <span className="text-lg sm:text-xl font-semibold text-green-700">
                Macros & Meals
              </span>
            </div>
            <p className="text-gray-600 text-sm sm:text-base max-w-sm mb-4 sm:mb-6">
              Professional nutrition coaching and meal planning to help you
              achieve your health goals.
            </p>

            {/* Social Media Icons */}
            <div className="flex items-center space-x-3">
              <Link
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-100 hover:bg-green-100 rounded-full flex items-center justify-center transition-colors"
              >
                <Image
                  src={facebookIcon}
                  alt="Facebook"
                  width={24}
                  height={24}
                />
              </Link>
              <Link
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-100 hover:bg-green-100 rounded-full flex items-center justify-center transition-colors"
              >
                <Image
                  src={instagramIcon}
                  alt="Instagram"
                  width={24}
                  height={24}
                />
              </Link>
              <Link
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-100 hover:bg-green-100 rounded-full flex items-center justify-center transition-colors"
              >
                <Image src={twitterIcon} alt="Twitter" width={20} height={24} />
              </Link>
              <Link
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-100 hover:bg-green-100 rounded-full flex items-center justify-center transition-colors"
              >
                <Image src={youtubeIcon} alt="YouTube" width={24} height={16} />
              </Link>
            </div>
          </div>

          {/* Footer Links */}
          <div className="flex flex-row flex-wrap flex-1 justify-start gap-8 sm:gap-16">
            {/* Company Column */}
            <div className="flex flex-col min-w-[120px]">
              <h3 className="text-gray-900 font-semibold text-base mb-3">Company</h3>
              <div className="flex flex-col space-y-1.5 text-sm sm:text-base">
                <button
                  onClick={() => scrollToSection("services")}
                  className="text-gray-600 hover:text-green-600 transition-colors text-left py-1"
                >
                  Services
                </button>
                <Link
                  href={Routes.publicCalculator}
                  className="text-gray-600 hover:text-green-600 py-1 transition-colors"
                >
                  BMR/TDEE Calculator
                </Link>
                <Link
                  href={Routes.aboutus}
                  className="text-gray-600 hover:text-green-600 py-1 transition-colors"
                >
                  About Us
                </Link>
                <Link
                  href={Routes.successstories}
                  className="text-gray-600 hover:text-green-600 py-1 transition-colors"
                >
                  Success Stories
                </Link>
                <Link
                  href={Routes.blog}
                  className="text-gray-600 hover:text-green-600 py-1 transition-colors"
                >
                  Blog
                </Link>
              </div>
            </div>

            {/* Support Column */}
            <div className="flex flex-col min-w-[120px]">
              <h3 className="text-gray-900 font-semibold text-base mb-3">Support</h3>
              <div className="flex flex-col space-y-1.5 text-sm sm:text-base">
                <Link
                  href="/contact-us"
                  className="text-gray-600 hover:text-green-600 py-1 transition-colors"
                >
                  Contact Us
                </Link>
                <Link
                  href="/faq"
                  className="text-gray-600 hover:text-green-600 py-1 transition-colors"
                >
                  FAQ
                </Link>
                <Link
                  href="/privacy-policy"
                  className="text-gray-600 hover:text-green-600 py-1 transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  href="/terms-condition"
                  className="text-gray-600 hover:text-green-600 py-1 transition-colors"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Copyright */}
        <div className="border-t border-gray-200 mt-8 pt-6">
          <div className="flex flex-col sm:flex-row sm:justify-center">
            <p className="text-gray-500 text-xs sm:text-sm text-center">
              {getCopyrightHTML()} Macros & Meals. All rights reserved. |
              Designed for optimal health and nutrition.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default HomePageFooter;
